import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { PageLinkModel } from "@insite/client-framework/Services/ContentService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getIsPunchOutSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import cancelPunchOut from "@insite/client-framework/Store/Context/Handlers/CancelPunchOut";
import signOut from "@insite/client-framework/Store/Context/Handlers/SignOut";
import { getPageLinkByPageType, LinkModel } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon from "@insite/mobius/Icon/Icon";
import X from "@insite/mobius/Icons/X";
import Link from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

export interface GlobalAccountMenuStyles {
    menuWrapper?: GridContainerProps;
    buttonMenuWrapper?: GridItemProps;
    buttonMenuItems?: InjectableCss;
    headerMenuWrapper?: GridItemProps;
    redUnderline?: InjectableCss;
    greyUnderline?: InjectableCss;
    headerMenuItems?: GridContainerProps;
    menuItem?: GridItemProps;
    linkItem?: InjectableCss;
    userGreeting?: InjectableCss;
    userName?: InjectableCss;
    icon?: InjectableCss;
    activeIcon?: InjectableCss;
    menutriangle?: InjectableCss;
    titleMenuHeaderSpacedTypography?: TypographyPresentationProps;
    titleMenuHeaderTypography?: TypographyPresentationProps;
    signOutButton?: ButtonPresentationProps;
}

const mapStateToProps = (state: ApplicationState) => ({
    userName: state.context.session.userName,
    myAccountPageLink: getPageLinkByPageType(state, "MyAccountPage"),
    isPunchOutSession: getIsPunchOutSession(state),
});

const mapDispatchToProps = {
    signOut,
    cancelPunchOut,
};

const enum fields {
    menuHeader1 = "menuHeader1",
    menuHeader2 = "menuHeader2",
    menuHeader3 = "menuHeader3",
    links = "links",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.menuHeader1]: string;
        [fields.menuHeader2]: string;
        [fields.menuHeader3]: string;
        [fields.links]: PageLinkModel[];
    };
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export const GlobalAccountMenuStyles: GlobalAccountMenuStyles = {
    menuWrapper: {
        css: css`
            height: auto;
            width: 54.1875rem;
            top: 5rem;
            right: 10%;
            z-index: 1;
            position: absolute;
        `,
    },
    buttonMenuWrapper: {
        css: css`
            height: 100%
            width: 14.9375rem;
            background-color: #ffffff;
        `,
    },
    buttonMenuItems: {
        css: css`
            margin: 1.25rem;
        `,
    },
    headerMenuWrapper: {
        css: css`
            height: 100%;
            width: 39.25rem;
            background-color: #f3f4f5;
        `,
    },
    headerMenuItems: {
        css: css`
            height: 9.5625rem;
            width: 35.75rem;
            margin: 1.5625rem;
            margin-left: 1.375rem;
        `,
    },
    titleMenuHeaderTypography: {
        css: css`
            color: #c63527;
            font-family: 'Roboto', sans-serif;
            font-weight: 500;
            font-size: .875rem;
            letter-spacing: .01rem;
            line-height: 1.4375rem;
            margin-bottom: 1rem;
        `,
        ellipsis: true,
        transform: "uppercase",
    },
    signOutButton: {
        css: css`
            display: flex;
            align-items: center;
            justify-content: space-between;
            box-sizing: border-box;
            height: 2.5rem;
            width: 8.0625rem;
            border: 1px solid #002554;
            border-radius: .125rem;
            background-color: #ffffff;
            color: #002554;
            margin-top: 2.375rem;
            font-family: 'Roboto', sans-serif;
            font-size: .9375rem;
            line-height: 1.3125rem;
            text-align: center;
            &:hover {
                border: 1px solid #2060ad;
                border-radius: .125rem;
                background-color: #2060ad;
                color: #ddecfe;
                font-family: 'Roboto', sans-serif;
                font-size: .875rem;
                font-weight: bold;
                letter-spacing: 0;
                line-height: 1.3125rem;
                text-align: center;
            }
        `,
    },
    redUnderline: {
        css: css`
            height: .1875rem;
            width: 1.25rem;
            background-color: #c63527;
        `,
    },
    greyUnderline: {
        css: css`
            box-sizing: border-box;
            height: .0625rem;
            width: 11.1875rem;
            border: 1px solid rgba(194, 209, 217, .45);
        `,
    },
    menuItem: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    linkItem: {
        css: css`
            color: #002554;
            font-family: 'Roboto', sans-serif;
            font-size: .875rem;
            letter-spacing: -.014rem;
            line-height: 2.1875rem;
            text-transform: capitalize;
        `,
    },
    userGreeting: {
        css: css`
            display: flex;
            color: #002554;
            font-family: 'Roboto', sans-serif;
            font-size: 1.125rem;
            font-weight: 500;
            line-height: 1.9375rem;
            text-transform: capitalize;
        `,
    },
    userName: {
        css: css`
            margin-left: .625rem;
            text-transform: capitalize;
        `,
    },
    icon: {
        css: css`
            color: #002554;
            &:hover {
                color: #ddecfe;
                font-family: 'Roboto', sans-serif;
                font-size: .875rem;
                font-weight: bold;
            }
        `,
    },
    activeIcon: {
        css: css`
            color: #ffffff;
            font-family: 'Roboto', sans-serif;
            font-size: .875rem;
            font-weight: bold;
        `,
    },
    menutriangle: {
        css: css`
            width: 0;
            height: 0;
            border: 15px solid transparent;
            border-bottom-color: #ffffff;
            top: 2.1875rem;
            right: 25%;
            z-index: 1;
            position: absolute;
        `,
    },
};

export const EditableGlobalAccountMenu: React.FC<Props> = props => {
    const [buttonIcon, setButtonIcon] = useState(false);
    const styles = GlobalAccountMenuStyles;
    const settingsArr: PageLinkModel[] = [];
    const purchasesArr: PageLinkModel[] = [];
    // eslint-disable-next-line no-console

    props.myAccountPageLink?.children?.map(link => {
        if (link?.type === "AccountSettingsPage") {
            settingsArr.push(link);
        }
        if (link?.type === "AddressesPage") {
            settingsArr.push(link);
        }
        if (link?.type === "SavedPaymentsPage") {
            settingsArr.push(link);
        }
        if (link?.type === "OrderHistoryPage") {
            purchasesArr.push(link);
        }
        if (link?.type === "SavedOrderListPage") {
            purchasesArr.push(link);
        }
    });

    const onSignOutHandler = () => {
        props.isPunchOutSession ? cancelPunchOut() : signOut();
    };

    return (
        <>
            <StyledWrapper {...styles.menutriangle}></StyledWrapper>
            <GridContainer {...styles.menuWrapper}>
                <GridItem width={3} {...styles.buttonMenuWrapper}>
                    <StyledWrapper {...styles.buttonMenuItems}>
                        <StyledWrapper {...styles.userGreeting}>
                            <Typography>Hi</Typography>
                            <Typography {...styles.userName}>{props.userName}</Typography>
                        </StyledWrapper>
                        <StyledWrapper {...styles.redUnderline}></StyledWrapper>
                        <Button
                            {...styles.signOutButton}
                            onClick={onSignOutHandler}
                            onMouseEnter={() => setButtonIcon(true)}
                            onMouseLeave={() => setButtonIcon(false)}
                        >
                            Sign Out
                            {buttonIcon ? <Icon {...styles.activeIcon} src={X} /> : <Icon {...styles.icon} src={X} />}
                        </Button>
                    </StyledWrapper>
                </GridItem>
                <GridItem width={9} {...styles.headerMenuWrapper}>
                    <GridContainer {...styles.headerMenuItems}>
                        <GridItem width={4} {...styles.menuItem}>
                            <Typography {...styles.titleMenuHeaderTypography}>
                                {props.fields.menuHeader1}
                            </Typography>
                            {settingsArr.map(setting => (
                                <StyledWrapper key={setting?.id}>
                                    <Link href={setting?.url}>
                                        <Typography {...styles.linkItem}>{setting?.title}</Typography>
                                    </Link>
                                    <StyledWrapper {...styles.greyUnderline}></StyledWrapper>
                                </StyledWrapper>
                            ))}
                        </GridItem>
                        <GridItem width={4} {...styles.menuItem}>
                            <Typography {...styles.titleMenuHeaderTypography}>
                                {props.fields.menuHeader2}
                            </Typography>
                            {purchasesArr.map(setting => (
                                <StyledWrapper key={setting?.id}>
                                    <Link href={setting?.url}>
                                        <Typography {...styles.linkItem}>{setting?.title}</Typography>
                                    </Link>
                                    <StyledWrapper {...styles.greyUnderline}></StyledWrapper>
                                </StyledWrapper>
                            ))}
                        </GridItem>
                        <GridItem width={4} {...styles.menuItem}>
                            <Typography {...styles.titleMenuHeaderTypography}>
                                {props.fields.menuHeader3}
                            </Typography>
                        </GridItem>
                    </GridContainer>
                </GridItem>
            </GridContainer>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(EditableGlobalAccountMenu),
    definition: {
        displayName: "Editable Account Menu",
        fieldDefinitions: [
            {
                name: fields.menuHeader1,
                fieldType: "Translatable",
                editorTemplate: "TextField",
                defaultValue: "Settings",
                isRequired: true,
            },
            {
                name: fields.menuHeader2,
                fieldType: "Translatable",
                editorTemplate: "TextField",
                defaultValue: "Purchases",
                isRequired: true,
            },
            {
                name: fields.menuHeader3,
                fieldType: "Translatable",
                editorTemplate: "TextField",
                defaultValue: "Utilities",
                isRequired: true,
            },
            {
                name: fields.links,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields, state) => {
                    const {
                        destination: { type },
                        overriddenTitle,
                    } = item.fields;
                    // eslint-disable-next-line no-console

                    if (overriddenTitle) {
                        return overriddenTitle;
                    }
                    if (type === "") {
                        const link = getPageLinkByPageType(state, "MyAccountPage");
                        // eslint-disable-next-line no-console

                        return link?.title;
                    }
                },
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: "destination",
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Page",
                        },
                        isRequired: true,
                    },
                    {
                        name: "Title",
                        displayName: "Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "excludeFromNavigation",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                ],
            },
        ],
        group: "Basic",
    },
};

export default widgetModule;
